import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { HardDrive, Radio, Disc, Server } from 'lucide-react';

interface SensorStorage {
  project_id: string;
  project_name: string;
  dataset_id: string;
  label: string;
  mac_id: string | null;
  gateway_id: string | null;
  realtime: { n_rows: number; approx_bytes: number; first: string | null; last: string | null };
  recordings: { count: number; n_rows: number; approx_bytes: number };
}

interface StorageResponse {
  hypertables: Record<string, { total_bytes: number }>;
  sensors: SensorStorage[];
}

// Tamanho aproximado (pg_column_size, lógico/descomprimido) - não é o espaço físico
// real em disco, que tende a ser menor graças à compressão do TimescaleDB.
const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const Storage: React.FC = () => {
  const [storage, setStorage] = useState<StorageResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStorage = async () => {
      try {
        const { data } = await api.get('/admin/storage');
        setStorage(data);
      } catch (err) {
        console.error('Falha ao carregar uso de armazenamento');
      } finally {
        setLoading(false);
      }
    };
    fetchStorage();
  }, []);

  // Agrupa por gateway para dar a visão "quanto espaço cada gateway ativo ocupa".
  const byGateway = useMemo(() => {
    if (!storage) return [];
    const groups: { [key: string]: { gateway_id: string; sensors: SensorStorage[]; total_bytes: number } } = {};
    for (const s of storage.sensors) {
      const key = s.gateway_id || 'Desconhecido';
      if (!groups[key]) groups[key] = { gateway_id: key, sensors: [], total_bytes: 0 };
      groups[key].sensors.push(s);
      groups[key].total_bytes += s.realtime.approx_bytes + s.recordings.approx_bytes;
    }
    return Object.values(groups).sort((a, b) => b.total_bytes - a.total_bytes);
  }, [storage]);

  const totalRealtime = storage?.hypertables?.realtime_readings?.total_bytes || 0;
  const totalRecordings = storage?.hypertables?.recording_readings?.total_bytes || 0;

  return (
    <div className="max-w-6xl mx-auto space-y-8 py-10 animate-in fade-in duration-300">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-blue-600/10 text-blue-600 rounded-2xl"><HardDrive size={22} /></div>
        <div>
          <h2 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">Armazenamento</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5">
            {loading ? 'A carregar…' : 'Espaço ocupado por sensor e por gateway em tempo real'}
          </p>
        </div>
      </div>

      {!loading && storage && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-6 flex items-center gap-4 shadow-sm">
              <div className="p-3 bg-emerald-500/10 text-emerald-500 rounded-2xl"><Radio size={20} /></div>
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">Tempo real (31 dias)</p>
                <p className="text-2xl font-semibold text-slate-900 dark:text-white">{formatBytes(totalRealtime)}</p>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-6 flex items-center gap-4 shadow-sm">
              <div className="p-3 bg-amber-500/10 text-amber-500 rounded-2xl"><Disc size={20} /></div>
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">Gravações (sem limite)</p>
                <p className="text-2xl font-semibold text-slate-900 dark:text-white">{formatBytes(totalRecordings)}</p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {byGateway.map((group) => (
              <div key={group.gateway_id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl overflow-hidden shadow-sm">
                <div className="p-4 flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-2">
                    <Server size={16} className="text-slate-400" />
                    <span className="font-semibold text-slate-700 dark:text-slate-200">{group.gateway_id}</span>
                  </div>
                  <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{formatBytes(group.total_bytes)}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-700/60">
                        <th className="p-4 text-xs font-medium text-slate-400">Sensor</th>
                        <th className="p-4 text-xs font-medium text-slate-400">Projeto</th>
                        <th className="p-4 text-xs font-medium text-slate-400">Amostras (31d)</th>
                        <th className="p-4 text-xs font-medium text-slate-400">Tempo real</th>
                        <th className="p-4 text-xs font-medium text-slate-400">Gravações</th>
                        <th className="p-4 text-xs font-medium text-slate-400">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                      {group.sensors.map((s) => (
                        <tr key={s.dataset_id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                          <td className="p-4 text-sm font-medium text-slate-700 dark:text-slate-200">
                            {s.label}
                            {s.mac_id && <span className="block text-xs text-slate-400 font-mono">{s.mac_id}</span>}
                          </td>
                          <td className="p-4 text-sm text-slate-500 dark:text-slate-400">{s.project_name}</td>
                          <td className="p-4 text-sm text-slate-500 dark:text-slate-400">{s.realtime.n_rows.toLocaleString('pt-PT')}</td>
                          <td className="p-4 text-sm text-slate-500 dark:text-slate-400">{formatBytes(s.realtime.approx_bytes)}</td>
                          <td className="p-4 text-sm text-slate-500 dark:text-slate-400">
                            {formatBytes(s.recordings.approx_bytes)}
                            {s.recordings.count > 0 && <span className="block text-xs text-slate-400">{s.recordings.count} gravação(ões)</span>}
                          </td>
                          <td className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-200">
                            {formatBytes(s.realtime.approx_bytes + s.recordings.approx_bytes)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {byGateway.length === 0 && (
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-16 text-center text-slate-400">
                Nenhum sensor em tempo real encontrado.
              </div>
            )}
          </div>

          <p className="text-xs text-slate-400">
            Os valores de "Tempo real" e "Gravações" são aproximados (tamanho lógico antes de compressão) — o espaço físico
            real em disco tende a ser menor, já que o TimescaleDB comprime dados com mais de 1 dia.
          </p>
        </>
      )}
    </div>
  );
};

export default Storage;
